import type { MinimumStationsGroup, Station } from '@mi-front/core';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { useStationsGroupsQuery } from '../stations/hooks/useStationsGroupsQuery';
import { useStationsQuery } from '../stations/hooks/useStationsQuery';

export interface AppDataContextValue {
  stations: Station[];
  stationsGroups: MinimumStationsGroup[];
  refetchStations: () => void;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

export function AppDataProvider({
  children,
  stations,
  stationsGroups,
}: {
  children: ReactNode;
  stations: Station[];
  stationsGroups: MinimumStationsGroup[];
}) {
  const queryClient = useQueryClient();

  queryClient.setQueryData(['stations'], stations);
  queryClient.setQueryData(['stations-groups'], stationsGroups);

  const stationsQuery = useStationsQuery();
  const groupsQuery = useStationsGroupsQuery();

  const refetchStations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: useStationsQuery.queryKey() });
  }, [queryClient]);

  const value: AppDataContextValue = {
    stations: stationsQuery.data ?? [],
    stationsGroups: groupsQuery.data ?? [],
    refetchStations,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/**
 * Returns app data (stations and station groups) from React Query cache.
 * Must be used inside AppDataProvider. For components outside the provider, use useStationsQuery / useStationsGroupsQuery (or useStations / useStationsGroups) directly.
 */
export function useAppData(): AppDataContextValue {
  const ctx = useContext(AppDataContext);
  if (!ctx) {
    throw new Error('useAppData must be used within AppDataProvider');
  }
  return ctx;
}
